import { ChangeEvent, KeyboardEvent, RefObject, SyntheticEvent, useEffect, useRef, useState } from "react"
import { Alert, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Table } from "react-bootstrap"
import ContentBody from "../../components/ContentBody"
import ContentHeader from "../../components/ContentHeader"
import Pagination from "../../components/Pagination"
import useAppContext from "../../hooks/useAppContext"
import ComboService, { ICombo } from "../../services/ComboService"
import Configs from "../../Configs"
import DeleteConfirmation from "../../components/DeleteConfirmation"
import PlaceService, { IPlace } from "../../services/PlaceService"
import { SelectInstance } from "react-select"
import AsyncSelect from "react-select/async"
import { SelectOptionType } from "../../constants/SelectOptionType"
import Utils from "../../utils/Utils"
import useAuthContext from "../../hooks/useAuthContext"
import Permissions from "../../constants/Permissions"

function Combos() {
    const { hasPermission } = useAuthContext()
    const { state } = useAppContext()

    const [tableData, setTableData] = useState({
        data: new Array<ICombo>(),
        hasNext: false,
        hasPrevious: false,
        pageIndex: 1,
        pageSize: Configs.DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0
    })

    const initFormData: ICombo = {
        id: null,
        name: "",
        description: "",
        percentageDiscount: null,
        isUsed: true,
        places: []
    }

    const [message, setMessage] = useState("")
    const [messageFormModal, setMessageFormModal] = useState("")
    const [validatedFormModal, setValidatedFormModal] = useState(false)
    const [formData, setFormData] = useState(initFormData)
    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [deletedItem, setDeletedItem] = useState({
        id: 0,
        name: ""
    })

    const keywordRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<HTMLFormElement>(null)

    const FilterFn = async (pageIndex: number) => {
        if (state.currentProviderId) {
            let pageSize = Configs.DEFAULT_PAGE_SIZE
            let keyword = keywordRef.current?.value || ""

            const res = await ComboService.filter(state.currentProviderId, pageIndex, pageSize, keyword)

            if (res?.isSuccess) {
                setTableData(res.data)
            } else {
                console.log(res?.message)
            }

            setCurrentPageIndex(pageIndex)
        }
    }

    useEffect(() => {
        keywordRef.current?.focus()

        FilterFn(1)
    }, [state.currentProviderId])

    const handleSearch = () => {
        FilterFn(1)
    }

    const handleSearchInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            FilterFn(1)
        }
    }

    const handlePageChange = (pageNumber: number) => {
        FilterFn(pageNumber)
    }

    const handleFormControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        var name = e.target.name
        var value = e.target.value

        if (name === "percentageDiscount") {
            setFormData({ ...formData, [name]: value ? parseInt(value) : null })
        } else {
            setFormData({ ...formData, [name]: value })
        }
    }

    const handleAddNew = () => {
        setFormData(initFormData)

        handleShowModal()
    }

    const handleEdit = async (id: number | null) => {
        if (state.currentProviderId && id) {
            const res = await ComboService.get(state.currentProviderId, id)

            if (res?.isSuccess) {
                setFormData({ ...res.data, places: res.data.places.map((x: IPlace) => { return { ...x, placeId: x.id } }) })

                handleShowModal()
            } else {
                console.log(res?.message)
            }
        }
    }

    const handleSave = async (e: SyntheticEvent) => {
        e.preventDefault()

        if (state.currentProviderId) {
            if (formRef.current?.checkValidity()) {
                if (formData.id) {
                    const res = await ComboService.update(state.currentProviderId, formData.id, formData)

                    if (res?.isSuccess) {
                        FilterFn(currentPageIndex)
                        handleCloseModal()
                        setMessage(res.message)
                    } else {
                        setMessageFormModal(res?.message)
                    }
                } else {
                    const res = await ComboService.create(state.currentProviderId, formData)

                    if (res?.isSuccess) {
                        FilterFn(1)
                        handleCloseModal()
                        setMessage(res.message)
                    } else {
                        setMessageFormModal(res?.message)
                    }
                }
            } else {
                setValidatedFormModal(true)
            }
        }
    }

    const [showModal, setShowModal] = useState(false)
    const handleCloseModal = () => {
        setShowModal(false)
        setValidatedFormModal(false)
        setMessageFormModal("")
    }
    const handleShowModal = () => {
        setShowModal(true)
        setMessage("")
    }

    const modalTitle = formData.id ? "Edit Combo" : "Create New Combo"

    //Delete
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false)

    const hideConfirmationModal = () => {
        setDeletedItem({
            id: 0,
            name: ""
        })

        setDisplayConfirmationModal(false)
    }

    const showDeleteConfirmation = (id: number | null, name: string) => {
        setDeletedItem({
            id: id || 0,
            name: name
        })

        setDisplayConfirmationModal(true)
        setMessage("")
    }

    const handleDelete = async () => {
        if (state.currentProviderId && deletedItem.id) {
            const res = await ComboService.delete(state.currentProviderId, deletedItem.id)

            if (res?.isSuccess) {
                FilterFn(currentPageIndex)
            }

            hideConfirmationModal()
            setMessage(res.message)
        }
    }

    const handleIsUsedSwitch = async (id: number | null) => {
        if (state.currentProviderId && id) {
            const res = await ComboService.changeIsUsed(state.currentProviderId, id)

            if (res?.isSuccess) {
                FilterFn(currentPageIndex)
            } else {
                console.log(res?.message)
            }
        }
    }

    //AsyncSelect
    const [selectedOption, setSelectedOption] = useState(null as SelectOptionType | null)
    const asyncRef = useRef<SelectInstance<SelectOptionType> | null>(null)

    const handleSelectChange = async (option: SelectOptionType | null) => {
        setSelectedOption(option)

        if (state.currentProviderId && option?.value) {
            let value = option?.value as number

            if (formData.places.filter(x => x.id === value).length === 0) {
                var res = await PlaceService.get(state.currentProviderId, value)

                if (res.isSuccess) {
                    formData.places.push({ ...res.data, placeId: res.data.id })

                    setFormData(formData)

                    setSelectedOption(null)
                    asyncRef.current?.focus()
                }
            }
        }
    }

    const filterPlaces = async (inputValue: string) => {
        var options: SelectOptionType[] = []

        if (state.currentProviderId) {
            var res = await PlaceService.search(state.currentProviderId, inputValue)

            if (res.isSuccess && res.data) {
                options = res.data.map((item: IPlace) => {
                    return { value: item.id, label: item.name }
                })
            }
        }

        return options
    }

    const loadPlaceOptions = (inputValue: string) => new Promise<SelectOptionType[]>((resolve) => {
        resolve(filterPlaces(inputValue))
    })

    const handleRemovePlace = (placeId: number | null) => {
        setFormData({ ...formData, places: formData.places.filter(x => x.id !== placeId) });
    }

    return (
        <>
            <ContentHeader title="Combos" />

            <ContentBody>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Header>
                                    {hasPermission(Permissions.Combos.Create) &&
                                        <Button variant="info" size="sm" onClick={handleAddNew}>
                                            <i className="fas fa-plus"></i> Create new
                                        </Button>
                                    }

                                    <div className="card-tools">
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="search"
                                                placeholder="Search"
                                                ref={keywordRef as RefObject<HTMLInputElement>}
                                                onKeyUp={handleSearchInputKeyPress}
                                            />
                                            <Button variant="info" size="sm" onClick={handleSearch}>
                                                <i className="fas fa-search"></i>
                                            </Button>
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {message !== "" && <Alert variant="info" onClose={() => setMessage("")} dismissible>{message}</Alert>}
                                    <Table responsive striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Places</th>
                                                <th>Discount (%)</th>
                                                <th>Is Used</th>
                                                <th style={{ textAlign: "center", width: "80px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                tableData.data.map(item =>
                                                    <tr key={item.id}>
                                                        <td>{item.name}</td>
                                                        <td>{item.description.split("\n").map((line, index) => {
                                                            return <p key={index}>{line}</p>
                                                        })}</td>
                                                        <td>
                                                            <ul>
                                                                {item.places.map(place => {
                                                                    return <li key={place.id}>{place.name}</li>
                                                                })}
                                                            </ul>
                                                        </td>
                                                        <td>{item.percentageDiscount}</td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <Form.Check type="switch" id={"combo_isused_" + item.id} checked={item.isUsed} onChange={() => handleIsUsedSwitch(item.id)} disabled={!hasPermission(Permissions.Combos.Edit)} />
                                                        </td>
                                                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                            {hasPermission(Permissions.Combos.Edit) &&
                                                                <Button variant="warning" className="mr-1" size="sm" onClick={() => handleEdit(item.id)}><i className="fas fa-pencil-alt"></i> Edit</Button>
                                                            }
                                                            {hasPermission(Permissions.Combos.Delete) &&
                                                                <Button variant="danger" size="sm" onClick={() => showDeleteConfirmation(item.id, item.name)}><i className="fas fa-trash-alt"></i> Delete</Button>
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer>
                                    <Pagination hasNext={tableData.hasNext} hasPrevious={tableData.hasPrevious} pageIndex={tableData.pageIndex} pageSize={tableData.pageSize} totalPages={tableData.totalPages} totalRecords={tableData.totalRecords} handlePageChange={handlePageChange} />
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </ContentBody>

            <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validatedFormModal} ref={formRef as RefObject<HTMLFormElement>}>
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>Name</Form.Label>
                            <Form.Control type="text" name="name" required value={formData.name} onChange={(e) => handleFormControlChange(e as any)} />
                            <Form.Control.Feedback type="invalid">Name is required.</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="description">
                            <Form.Label>Description</Form.Label>
                            <Form.Control as="textarea" name="description" value={formData.description} onChange={(e) => handleFormControlChange(e as any)} />
                        </Form.Group>
                        <fieldset className="mb-3">
                            <legend>Places</legend>
                            <AsyncSelect className="mb-3" isClearable value={selectedOption} loadOptions={loadPlaceOptions} onChange={handleSelectChange} ref={asyncRef} placeholder="Enter the place name to search." noOptionsMessage={({ inputValue }) => !inputValue ? "Enter the place name to search." : "There are no places!"} />
                            <Table responsive hover bordered size="sm" className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th style={{ textAlign: "right" }}>Price</th>
                                        <th>Currency</th>
                                        <th style={{ width: "40px" }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.places.map((place: IPlace) =>
                                        <tr key={place.id}>
                                            <td>{place.name}</td>
                                            <td style={{ textAlign: "right" }}>{Utils.formatNumber(place.price)}</td>
                                            <td>{place.currency || ""}</td>
                                            <td>
                                                <Button variant="light" size="sm" onClick={() => handleRemovePlace(place.id)}><i className="fas fa-times"></i></Button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </fieldset>
                        <Form.Group controlId="percentageDiscount">
                            <Form.Label>Discount (%)</Form.Label>
                            <Form.Control type="number" min={0} max={100} name="percentageDiscount" required value={formData.percentageDiscount || ""} onChange={(e) => handleFormControlChange(e as any)} />
                            <Form.Control.Feedback type="invalid">Discount is required.</Form.Control.Feedback>
                        </Form.Group>
                    </Form>
                    {messageFormModal !== "" && <Alert variant="danger" onClose={() => setMessageFormModal("")} dismissible>{messageFormModal}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={handleCloseModal}><i className="fas fa-times"></i> Cancel</Button>
                    <Button variant="info" onClick={handleSave}><i className="fas fa-save"></i> Save</Button>
                </Modal.Footer>
            </Modal>

            <DeleteConfirmation show={displayConfirmationModal} onConfirm={handleDelete} onHide={hideConfirmationModal} id={deletedItem.id} name={deletedItem.name} />
        </>
    )
}

export default Combos